import { Provider } from '@angular/core';
import { Route } from '@angular/router';
import { entityFeatureRouteGuard } from '@workbuddy/ui/v2/blocks/entity.record/entity.operation.route.guard';
import { ENTITY_INSPECTOR_OUTLET, EntityInspectorDeactivateSource, EntityInspectors } from './entity.inspector.contracts';
import { entityInspectorRouteGuard } from './entity.inspector.route.guard';
import { EntityInspectorService } from './entity.inspector.service';

/**
 * Provided on ShellComponent and destroyed with that shell, so nested boards inject the same
 * instance and never create a nested host.
 *
 * The **service** implements both tokens because the route guard must inject the deactivate
 * source before the host exists.
 */
export function provideEntityInspectors(): Provider[] {

  return [
    EntityInspectorService,
    { provide: EntityInspectors, useExisting: EntityInspectorService },
    { provide: EntityInspectorDeactivateSource, useExisting: EntityInspectorService },
  ];
}

/**
 * **One route config, not one per operation.** The operation param keeps operation changes on the same host.
 *
 * **No literal path prefix.** Routes are matched per-outlet, so `inspector` is already the namespace
 * — a `inspector/` segment would be a second discriminator for a namespace that has one. A future
 * sibling on this outlet is just a literal route declared ahead of this wildcard, the same way the
 * board's `dialog` outlet gets by with bare `new` and `edit/:id`.
 *
 * Entity slug leads because it is the more significant discriminator, and it matches the noun-first shape
 * of every primary route (`finance-pro/jobs`, `crm/contact`).
 *
 * The record id remains a matrix param to match the generic entity-route shape.
 */
export const entityInspectorRoute: Route = {
  path: ':slug/:operation',
  outlet: ENTITY_INSPECTOR_OUTLET,
  loadComponent: () => import('./entity.inspector.host.component').then(m => m.EntityInspectorHostComponent),
  // Rollout flag only: the host owns rights because it may resolve a denied edit to preview.
  canActivate: [entityFeatureRouteGuard],
  canDeactivate: [entityInspectorRouteGuard],
  // The side nav builds itself from the shell route's children and reads `data.title` /
  // `data.restrictions` unguarded, so a child with no `data` throws and takes the whole nav with it.
  // `hidden` is the flag it already honours for a route that is not a navigation destination.
  data: { hidden: true, restrictions: [] },
};
